<!-- Single-choice brand picker shared by the single and bulk forms (KAN-363): one brand or a deliberate "All brands" -->
<ng-template #brandPicker let-target="target">
  <fieldset class="brand-picker" role="radiogroup" tabindex="-1" aria-required="true"
            [id]="'brand-fieldset-' + target" [attr.aria-invalid]="brandError(target) ? 'true' : null"
            [attr.aria-describedby]="brandError(target) ? 'brand-error-' + target : 'brand-hint-' + target">
    <legend>Eligible Brands *</legend>
    <div class="brand-picker-notice" role="status" *ngIf="target === 'single' && legacyBrandNotice">
      <span>{{ legacyBrandNotice }}</span>
      <span class="brand-picker-current" *ngIf="legacyBrandCurrent">Currently: {{ legacyBrandCurrent }}</span>
    </div>
    <label class="brand-option" *ngFor="let b of brandOptions" [class.selected]="selectedBrand[target] === b.id"
           [class.disabled]="isSaving">
      <input type="radio" [name]="'brandScope-' + target" [value]="b.id" [checked]="selectedBrand[target] === b.id"
             (change)="selectBrand(target, b.id)" [disabled]="isSaving">
      <span>{{ b.name }}</span>
    </label>
    <p class="brand-picker-empty" *ngIf="brandOptions.length === 0">No brands available.</p>
    <hr class="brand-picker-divider">
    <label class="brand-option brand-option-all" [class.selected]="selectedBrand[target] === allBrandsId"
           [class.disabled]="isSaving">
      <input type="radio" [name]="'brandScope-' + target" [value]="allBrandsId"
             [checked]="selectedBrand[target] === allBrandsId" (change)="selectBrand(target, allBrandsId)" [disabled]="isSaving">
      <span>
        All brands
        <span class="brand-option-desc">Valid for every brand, including brands added later.</span>
      </span>
    </label>
  </fieldset>
  <p class="brand-picker-error" role="alert" [id]="'brand-error-' + target" *ngIf="brandError(target) as brandErr">{{ brandErr }}</p>
</ng-template>

<div class="fade-in">
  <div class="content-section">
    <!-- Header -->
    <div class="section-header no-print">
      <div class="header-left">
        <h2>Coupon Management</h2>
      </div>
      <div class="header-actions">
        <button class="secondary-btn download-zip-btn" 
                (click)="downloadSelectedZippedVouchers()" 
                [disabled]="isDownloadingZip || getSelectedCount() === 0"
                [title]="getSelectedCount() === 0 ? 'Select at least one coupon below to download' : 'Download ' + getSelectedCount() + ' coupon vouchers as a ZIP file'">
          <span *ngIf="!isDownloadingZip">📦 Download ZIP ({{ getSelectedCount() }})</span>
          <span *ngIf="isDownloadingZip">⏳ {{ zipProgressText || 'Packaging ZIP...' }}</span>
        </button>
        <button *ngIf="canCreateCoupons" class="secondary-btn" (click)="openBulkModal()">
          <span>⚡ Bulk Generate</span>
        </button>
        <button *ngIf="canCreateCoupons" class="primary-btn" (click)="openAddModal()">
          <span>+ Add Coupon</span>
        </button>
      </div>
    </div>

    <!-- Search & Filter Bar -->
    <div class="search-filter-bar no-print">
      <input 
        type="text" 
        [(ngModel)]="searchTerm" 
        (ngModelChange)="applyFilterAndSort()" 
        placeholder="Search by code, title, recipient name or email..." 
        class="search-input"
      />

      <select [(ngModel)]="statusFilter" (ngModelChange)="applyFilterAndSort()" class="filter-select">
        <option value="all">All Statuses</option>
        <option value="active">Active</option>
        <option value="redeemed">Redeemed</option>
        <option value="expired">Expired</option>
        <option value="inactive">Inactive</option>
      </select>

      <select [(ngModel)]="typeFilter" (ngModelChange)="applyFilterAndSort()" class="filter-select">
        <option value="all">All Discount Types</option>
        <option value="fixed">Fixed ($)</option>
        <option value="percent">Percentage (%)</option>
        <option value="free_session">Free Session</option>
      </select>
    </div>

    <!-- Single Coupon Add/Edit Modal -->
    <div class="modal-overlay no-print" *ngIf="isModalOpen">
      <div class="modal-content fade-in wide">
        <div class="modal-sticky-header">
          <h3 class="modal-title">{{ editingId ? 'Edit Coupon' : 'Create New Coupon' }}</h3>
          <button class="close-btn-static" (click)="cancelEdit()" [disabled]="isSaving">&times;</button>
        </div>

        <div class="form-grid">
          <div class="form-group full-width">
            <label>Promotion Title / Description *</label>
            <input [(ngModel)]="newCoupon.title" [disabled]="isSaving" placeholder="e.g. Grand Opening $5 OFF" class="form-input">
          </div>

          <div class="form-group full-width">
            <ng-container *ngTemplateOutlet="brandPicker; context: { target: 'single' }"></ng-container>
            <span class="form-hint" id="brand-hint-single">Required. Choose one brand, or All brands. Store scope is not changed here.</span>
          </div>

          <div class="form-group">
            <label>Discount Type</label>
            <select [(ngModel)]="newCoupon.discountType" [disabled]="isSaving" class="form-select">
              <option value="fixed">Fixed Dollar ($)</option>
              <option value="percent">Percentage (%)</option>
              <option value="free_session">Free Session</option>
            </select>
          </div>

          <div class="form-group" *ngIf="newCoupon.discountType !== 'free_session'">
            <label>Discount Value {{ newCoupon.discountType === 'percent' ? '(%)' : '($)' }} *</label>
            <input type="number" step="0.5" [(ngModel)]="newCoupon.discountValue" [disabled]="isSaving" placeholder="5.00" class="form-input">
          </div>

          <div class="form-group">
            <label>Expiration Date (Pacific Time)</label>
            <input type="date" [(ngModel)]="newCoupon.expiresAt" [disabled]="isSaving" class="form-input">
            <span class="form-hint">Leave blank for no expiration (Stored in Pacific Time)</span>
          </div>

          <div class="form-group">
            <label>Usage Rule</label>
            <select [(ngModel)]="newCoupon.usageType" [disabled]="isSaving" class="form-select">
              <option value="single_use">Single Use (1 Time)</option>
              <option value="multi_use">Multi Use (Reusable)</option>
            </select>
          </div>

          <div class="form-group" *ngIf="newCoupon.usageType === 'multi_use'">
            <label>Max Redemptions</label>
            <input type="number" min="1" [(ngModel)]="newCoupon.maxRedemptions" [disabled]="isSaving" placeholder="100" class="form-input">
          </div>

          <div class="form-group">
            <label>Recipient Name</label>
            <input [(ngModel)]="newCoupon.recipientName" [disabled]="isSaving" placeholder="Optional recipient name" class="form-input">
          </div>

          <div class="form-group">
            <label>Recipient Email</label>
            <input type="email" [(ngModel)]="newCoupon.recipientEmail" [disabled]="isSaving" placeholder="Optional recipient email" class="form-input">
          </div>

          <div class="form-group full-width" *ngIf="!editingId && newCoupon.recipientEmail">
            <label style="display: flex; align-items: center; gap: 8px; cursor: pointer; color: #1e293b;">
              <input type="checkbox" [(ngModel)]="sendEmailOnCreate" [disabled]="isSaving">
              Send QR Coupon email directly to recipient upon creation
            </label>
          </div>

          <div class="form-group full-width" *ngIf="editingId">
            <label style="display: flex; align-items: center; gap: 8px; cursor: pointer; color: #1e293b;">
              <input type="checkbox" [(ngModel)]="newCoupon.isActive" [disabled]="isSaving">
              Coupon is Active
            </label>
          </div>
        </div>

        <div *ngIf="errorMessage" class="error-message mb">
          {{ errorMessage }}
        </div>

        <div class="modal-actions">
          <button class="secondary-btn" (click)="cancelEdit()" [disabled]="isSaving">Cancel</button>
          <button class="primary-btn" (click)="saveCoupon()" [disabled]="isSaving">
            {{ isSaving ? 'Saving...' : (editingId ? 'Update Coupon' : 'Create Coupon') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Bulk Generation Modal -->
    <div class="modal-overlay no-print" *ngIf="isBulkModalOpen">
      <div class="modal-content fade-in wide">
        <div class="modal-sticky-header">
          <h3 class="modal-title">⚡ Bulk Generate Coupons</h3>
          <button class="close-btn-static" (click)="closeBulkModal()" [disabled]="isSaving">&times;</button>
        </div>

        <div class="form-grid">
          <div class="form-group full-width">
            <label>Promotion Title *</label>
            <input [(ngModel)]="bulkForm.title" [disabled]="isSaving" placeholder="e.g. Summer Event $5 Promo" class="form-input">
          </div>

          <div class="form-group full-width">
            <ng-container *ngTemplateOutlet="brandPicker; context: { target: 'bulk' }"></ng-container>
            <span class="form-hint" id="brand-hint-bulk">Required. Every coupon in this batch gets the same brand scope.</span>
          </div>

          <div class="form-group">
            <label>Number of Coupons to Generate (1-200) *</label>
            <input type="number" min="1" max="200" [(ngModel)]="bulkForm.count" [disabled]="isSaving" class="form-input">
          </div>

          <div class="form-group">
            <label>Discount Type</label>
            <select [(ngModel)]="bulkForm.discountType" [disabled]="isSaving" class="form-select">
              <option value="fixed">Fixed Dollar ($)</option>
              <option value="percent">Percentage (%)</option>
              <option value="free_session">Free Session</option>
            </select>
          </div>

          <div class="form-group" *ngIf="bulkForm.discountType !== 'free_session'">
            <label>Discount Value {{ bulkForm.discountType === 'percent' ? '(%)' : '($)' }} *</label>
            <input type="number" step="0.5" [(ngModel)]="bulkForm.discountValue" [disabled]="isSaving" class="form-input">
          </div>

          <div class="form-group">
            <label>Expiration Date (Pacific Time)</label>
            <input type="date" [(ngModel)]="bulkForm.expiresAt" [disabled]="isSaving" class="form-input">
            <span class="form-hint">Leave blank for no expiration (Stored in Pacific Time)</span>
          </div>

          <div class="form-group">
            <label>Usage Rule</label>
            <select [(ngModel)]="bulkForm.usageType" [disabled]="isSaving" class="form-select">
              <option value="single_use">Single Use (1 Time each)</option>
              <option value="multi_use">Multi Use (Reusable)</option>
            </select>
          </div>

          <div class="form-group" *ngIf="bulkForm.usageType === 'multi_use'">
            <label>Max Redemptions per Coupon</label>
            <input type="number" min="1" [(ngModel)]="bulkForm.maxRedemptions" [disabled]="isSaving" class="form-input">
          </div>
        </div>

        <div *ngIf="errorMessage" class="error-message mb">
          {{ errorMessage }}
        </div>

        <div class="modal-actions">
          <button class="secondary-btn" (click)="closeBulkModal()" [disabled]="isSaving">Cancel</button>
          <button class="primary-btn" (click)="generateBulkCoupons()" [disabled]="isSaving">
            {{ isSaving ? 'Generating...' : ('Generate ' + bulkForm.count + ' Coupons') }}
          </button>
        </div>
      </div>
    </div>

    <!-- QR Code View / Print Modal -->
    <div class="modal-overlay" *ngIf="isQrModalOpen && selectedCouponForQr">
      <div class="modal-content fade-in printable-voucher">
        <div class="modal-sticky-header no-print">
          <h3 class="modal-title">Coupon Voucher & QR</h3>
          <button class="close-btn-static" (click)="closeQrModal()">&times;</button>
        </div>

        <div class="qr-preview-container">
          <div class="voucher-card">
            <div class="voucher-brand">{{ getIssuerName(selectedCouponForQr) }}</div>
            <div class="voucher-title">{{ selectedCouponForQr.title }}</div>
            <div class="voucher-discount">{{ getDiscountText(selectedCouponForQr) }}</div>
            <div class="voucher-brand-scope">Valid for: <strong>{{ getBrandLabel(selectedCouponForQr) }}</strong></div>

            <div class="voucher-qr" *ngIf="qrDataUrl">
              <img [src]="qrDataUrl" alt="Coupon QR Code">
            </div>

            <div class="voucher-code">{{ formatCode(selectedCouponForQr.code) }}</div>
            
            <div class="voucher-expiry">
              Valid Until: {{ formatDate(selectedCouponForQr.expiresAt) }} (Pacific Time)
            </div>

            <div *ngIf="selectedCouponForQr.recipientName && selectedCouponForQr.recipientName !== 'Printed'" style="font-size: 0.85rem; color: #475569; margin-top: 4px;">
              Issued to: <strong>{{ selectedCouponForQr.recipientName }}</strong>
            </div>

            <div class="voucher-instructions">
              How to use: Tap "Coupon" on the payment screen and hold this QR code up to the camera, or you can also manually enter the code shown above
            </div>
          </div>
        </div>

        <div class="modal-actions no-print" style="justify-content: space-between;">
          <button class="secondary-btn" (click)="openEmailModalFromQrModal()" [disabled]="isSendingEmail">
            <span>✉️ Email Voucher</span>
          </button>
          <div style="display: flex; gap: 8px;">
            <button class="secondary-btn" (click)="closeQrModal()">Close</button>
            <button class="primary-btn" (click)="printVoucher()">
              <span>🖨️ Print Voucher</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Email Coupon Modal (Centered Popup with Editable Title & Body) -->
    <div class="modal-overlay no-print" *ngIf="isEmailModalOpen && selectedCouponForEmail">
      <div class="modal-content fade-in email-modal-content">
        <div class="modal-sticky-header">
          <div class="email-modal-header-left">
            <h3 class="modal-title">✉️ Email Coupon Voucher</h3>
            <span class="email-modal-code-tag">{{ formatCode(selectedCouponForEmail.code) }}</span>
          </div>
          <button class="close-btn-static" (click)="closeEmailModal()" [disabled]="isSendingEmail">&times;</button>
        </div>

        <div class="email-modal-split-layout">
          <!-- Left Column: Form Editor (Title, Subject, Body, Recipient) -->
          <div class="email-editor-pane">
            <div class="editor-section-title">Edit Email Details</div>

            <div class="form-group">
              <label>Recipient Name</label>
              <input 
                type="text" 
                [(ngModel)]="emailModalRecipientName" 
                placeholder="e.g. Sarah Jenkins (Optional)" 
                class="form-input" 
                [disabled]="isSendingEmail"
              />
            </div>

            <div class="form-group">
              <label>Recipient Email Address *</label>
              <input 
                type="email" 
                [(ngModel)]="emailModalRecipientEmail" 
                placeholder="name@example.com" 
                class="form-input" 
                [disabled]="isSendingEmail"
                (input)="emailModalError = null"
              />
            </div>

            <div class="form-group">
              <label>Promotion Title / Badge *</label>
              <input 
                type="text" 
                [(ngModel)]="emailModalTitle" 
                placeholder="e.g. Grand Opening $5 OFF" 
                class="form-input" 
                [disabled]="isSendingEmail"
              />
            </div>

            <div class="form-group">
              <label>Email Subject Line *</label>
              <input 
                type="text" 
                [(ngModel)]="emailModalSubject" 
                placeholder="e.g. You have received a new Coupon from K-Photo LaVie!" 
                class="form-input" 
                [disabled]="isSendingEmail"
              />
            </div>

            <div class="form-group">
              <label>Email Message / Custom Body Note</label>
              <textarea 
                rows="4" 
                [(ngModel)]="emailModalBody" 
                placeholder="You have received an exclusive photo booth voucher for K-Photo LaVie! Scan your QR code at the kiosk payment screen or enter your 12-digit code." 
                class="form-textarea"
                [disabled]="isSendingEmail"
              ></textarea>
            </div>
          </div>

          <!-- Right Column: Live Email Inbox Preview -->
          <div class="email-preview-pane">
            <div class="preview-pane-header">
              <span class="preview-pane-title">Live Preview</span>
              <span class="preview-pane-badge">Inbox Simulated</span>
            </div>

            <div class="email-mockup-wrapper">
              <div class="email-mockup-window">
                <!-- Mockup Window Header -->
                <div class="email-mockup-header">
                  <div class="mockup-dots">
                    <span class="dot red"></span>
                    <span class="dot yellow"></span>
                    <span class="dot green"></span>
                  </div>
                  <div class="mockup-meta">
                    <div><span class="meta-label">From:</span> {{ getIssuerName(selectedCouponForEmail) }} &lt;noreply&#64;photolavie.com&gt;</div>
                    <div><span class="meta-label">To:</span> {{ emailModalRecipientEmail || '(recipient email address)' }}</div>
                    <div><span class="meta-label">Subject:</span> {{ emailModalSubject || ('You have received a new Coupon from ' + getIssuerName(selectedCouponForEmail) + '!') }}</div>
                  </div>
                </div>

                <!-- Rendered Email Content -->
                <div class="email-mockup-body">
                  <div class="email-rendered-card">
                    <div class="email-card-header">
                      <h2 class="email-brand-title">{{ getIssuerName(selectedCouponForEmail) }} Coupon</h2>
                      <p class="email-brand-subtitle">Your exclusive photo booth voucher</p>
                    </div>

                    <div class="email-card-inner">
                      <p class="email-greeting">
                        {{ emailModalRecipientName ? 'Hello ' + emailModalRecipientName + ',' : 'Hello,' }}
                      </p>
                      
                      <div class="email-custom-body-text">
                        {{ emailModalBody }}
                      </div>

                      <!-- Discount Box -->
                      <div class="email-discount-box">
                        <div class="email-discount-amount">{{ getDiscountText(selectedCouponForEmail) }}</div>
                        <div class="email-discount-title">{{ emailModalTitle || selectedCouponForEmail.title }}</div>
                      </div>

                      <!-- QR Code & Code Display -->
                      <div class="email-qr-section">
                        <div class="email-qr-frame">
                          <img [src]="getQrApiUrl(selectedCouponForEmail.code)" alt="Coupon QR Code" />
                        </div>
                        <div class="email-code-badge">
                          {{ formatCode(selectedCouponForEmail.code) }}
                        </div>
                      </div>

                      <!-- Expiration & Usage Meta -->
                      <div class="email-meta-details">
                        <div><strong>Expiration Date:</strong> <span class="exp-highlight">{{ formatDate(selectedCouponForEmail.expiresAt) }} (Pacific Time)</span></div>
                        <div><strong>Valid for:</strong> {{ getBrandLabel(selectedCouponForEmail) }}</div>
                        <div><strong>Usage:</strong> {{ selectedCouponForEmail.usageType === 'single_use' ? 'Single Use Only' : 'Multi-use Voucher' }}</div>
                      </div>
                    </div>

                    <div class="email-card-footer">
                      <p class="footer-inst">How to use: Tap "Coupon" on the payment screen and hold this QR code up to the camera, or you can also manually enter the code shown above</p>
                      <p class="footer-copy">© {{ getIssuerName(selectedCouponForEmail) }}. All rights reserved.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Alert messages inside modal -->
        <div *ngIf="emailModalError" class="drawer-alert error" style="margin-top: 16px;">
          ⚠️ {{ emailModalError }}
        </div>
        <div *ngIf="emailModalSuccess" class="drawer-alert success" style="margin-top: 16px;">
          ✅ {{ emailModalSuccess }}
        </div>

        <!-- Modal Footer Actions -->
        <div class="modal-actions" style="margin-top: 20px;">
          <button class="secondary-btn" (click)="closeEmailModal()" [disabled]="isSendingEmail">
            Cancel
          </button>
          <button class="primary-btn btn-send-email" (click)="sendEmailFromModal()" [disabled]="isSendingEmail">
            <span *ngIf="!isSendingEmail">📤 Send Coupon Email</span>
            <span *ngIf="isSendingEmail">⏳ Sending Email...</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Coupons Ledger Table -->
    <div style="overflow-x: auto;" class="no-print">
      <table class="ledger-table" *ngIf="filteredCoupons && filteredCoupons.length > 0; else noCoupons">
        <thead>
          <tr>
            <th class="col-checkbox">
              <input 
                type="checkbox" 
                class="row-checkbox"
                [checked]="isAllSelected()" 
                [indeterminate]="isSomeSelected()" 
                (change)="toggleSelectAll()" 
                title="Select / Deselect all"
              />
            </th>
            <th class="sortable" (click)="onSort('code')">
              Coupon Code (12-char)
              <span class="sort-icon" *ngIf="sortColumn === 'code'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="sortable" (click)="onSort('status')">
              Status
              <span class="sort-icon" *ngIf="sortColumn === 'status'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="sortable" (click)="onSort('title')">
              Promotion Type
              <span class="sort-icon" *ngIf="sortColumn === 'title'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="sortable" (click)="onSort('brands')">
              Brand
              <span class="sort-icon" *ngIf="sortColumn === 'brands'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="sortable" (click)="onSort('discountValue')">
              Discount
              <span class="sort-icon" *ngIf="sortColumn === 'discountValue'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="sortable" (click)="onSort('recipientEmail')">
              Recipient
              <span class="sort-icon" *ngIf="sortColumn === 'recipientEmail'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="sortable" (click)="onSort('expiresAt')">
              Expiration (Pacific Time)
              <span class="sort-icon" *ngIf="sortColumn === 'expiresAt'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="sortable" (click)="onSort('timesRedeemed')">
              Usage
              <span class="sort-icon" *ngIf="sortColumn === 'timesRedeemed'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="col-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let coupon of filteredCoupons" [class.selected-row]="selectedCouponCodes.has(coupon.code)">
            <td class="col-checkbox">
              <input 
                type="checkbox" 
                class="row-checkbox"
                [checked]="selectedCouponCodes.has(coupon.code)" 
                (change)="toggleSelectCoupon(coupon.code, $event)"
              />
            </td>
            <td>
              <div class="coupon-code-cell">
                <span>{{ formatCode(coupon.code) }}</span>
                <button class="copy-btn" (click)="copyCode(coupon.code)" title="Copy Code">
                  {{ copiedCode === coupon.code ? '✓' : '📋' }}
                </button>
              </div>
            </td>
            <td>
              <ng-container *ngIf="getCouponStatus(coupon) as st">
                <span class="status-badge" 
                      [ngClass]="st.class"
                      [class.clickable]="st.isRedeemed"
                      (click)="st.isRedeemed ? openRedemptionDetails(coupon) : null"
                      [title]="st.isRedeemed ? 'Click to view redemption audit details' : ''">
                  {{ st.label }}
                  <span *ngIf="st.isRedeemed" class="info-icon">ℹ️</span>
                </span>
              </ng-container>
            </td>
            <td>
              <span style="font-weight: 500;">{{ coupon.title }}</span>
            </td>
            <td>
              <span class="brand-chip" [class.brand-chip-unset]="!coupon.applicableBrands?.length" data-testid="coupon-brand-cell">{{ getBrandLabel(coupon) }}</span>
            </td>
            <td>
              <span style="font-weight: 600; color: #15803d;">
                {{ getDiscountText(coupon) }}
              </span>
            </td>
            <td>
              <div *ngIf="coupon.recipientName || coupon.recipientEmail; else noRecipient">
                <div style="font-weight: 500;">{{ coupon.recipientName }}</div>
                <div style="font-size: 0.8rem; color: #64748b;" *ngIf="coupon.recipientEmail">{{ coupon.recipientEmail }}</div>
              </div>
              <ng-template #noRecipient>
                <span style="color: #94a3b8; font-size: 0.85rem;">-</span>
              </ng-template>
            </td>
            <td>
              <span [style.color]="isCouponExpired(coupon) ? '#dc2626' : 'inherit'">
                {{ formatDate(coupon.expiresAt) }}
              </span>
            </td>
            <td>
              <span style="font-size: 0.85rem;">
                {{ coupon.timesRedeemed }} / {{ coupon.usageType === 'multi_use' ? coupon.maxRedemptions : 1 }}
                <span style="color: #64748b; font-size: 0.75rem;">({{ coupon.usageType === 'multi_use' ? 'Multi' : 'Single' }})</span>
              </span>
            </td>
            <td class="text-center">
              <button class="icon-action-btn mr" (click)="openEmailModal(coupon)" title="Email Coupon">✉️</button>
              <button class="icon-action-btn mr" (click)="viewQrModal(coupon)" title="View QR & Print">📱</button>
              <button class="icon-action-btn mr" (click)="editCoupon(coupon)" title="Edit">✏️</button>
              <button class="icon-action-btn" (click)="deleteCoupon(coupon)" title="Delete">🗑️</button>
            </td>
          </tr>
        </tbody>
      </table>

      <ng-template #noCoupons>
        <div class="empty-state">
          <p *ngIf="canCreateCoupons">No coupons found. <a href="javascript:void(0)" (click)="openAddModal()" style="color: #2563eb; text-decoration: underline;">Create one now</a> or <a href="javascript:void(0)" (click)="openBulkModal()" style="color: #2563eb; text-decoration: underline;">Bulk generate</a></p>
          <p *ngIf="!canCreateCoupons">No coupons found.</p>
        </div>
      </ng-template>
    </div>
  </div>
</div>

<!-- Redemption Details Modal -->
<div class="modal-overlay" *ngIf="isRedemptionModalOpen" (click)="closeRedemptionModal()">
  <div class="modal-content redemption-modal-content" (click)="$event.stopPropagation()">
    <div class="modal-header">
      <div class="modal-title-group">
        <h3 class="modal-main-title">🎟️ Voucher Redemption Details</h3>
        <p class="modal-subtitle">Audit record of when and where this coupon was redeemed at the kiosk</p>
      </div>
      <button class="close-btn" (click)="closeRedemptionModal()" title="Close">✕</button>
    </div>
    
    <div class="modal-body" *ngIf="selectedCouponForRedemption as coupon">
      <!-- Voucher Hero Header -->
      <div class="redemption-hero-card">
        <div class="hero-left">
          <div class="hero-label">Coupon Code</div>
          <div class="hero-code-wrapper">
            <span class="hero-code">{{ formatCode(coupon.code) }}</span>
            <button class="hero-copy-btn" (click)="copyCode(coupon.code)" title="Copy Code">
              {{ copiedCode === coupon.code ? '✓ Copied' : '📋 Copy' }}
            </button>
          </div>
          <div class="hero-tags">
            <span class="discount-pill">{{ getDiscountText(coupon) }}</span>
            <span class="usage-pill" *ngIf="coupon.usageType === 'multi_use'">Multi-Use ({{ coupon.timesRedeemed }}/{{ coupon.maxRedemptions }})</span>
            <span class="usage-pill" *ngIf="coupon.usageType !== 'multi_use'">Single-Use</span>
          </div>
        </div>
        <div class="hero-right">
          <span class="status-badge redeemed large">
            <span class="badge-icon">✓</span> Redeemed
          </span>
        </div>
      </div>

      <!-- Detail Info Grid -->
      <div class="redemption-audit-grid">
        <!-- Date & Time -->
        <div class="audit-card">
          <div class="audit-card-header">
            <span class="audit-icon">🕒</span>
            <span class="audit-label">Redeemed Date & Time (Pacific Time)</span>
          </div>
          <div class="audit-value-main">
            {{ formatLocalDateTime(coupon.redeemedAt) }}
          </div>
          <div class="audit-value-sub" *ngIf="coupon.redeemedAt">
            Timezone: Pacific Time (PT)
          </div>
        </div>

        <!-- Store Location -->
        <div class="audit-card">
          <div class="audit-card-header">
            <span class="audit-icon">🏬</span>
            <span class="audit-label">Store Location</span>
          </div>
          <div class="audit-value-main">
            {{ getStoreNameOnly(coupon.redeemedStoreId) }}
          </div>
          <div class="audit-value-sub" *ngIf="coupon.redeemedStoreId">
            ID: <span class="font-mono">{{ coupon.redeemedStoreId.trim() }}</span>
          </div>
        </div>

        <!-- Kiosk Device -->
        <div class="audit-card">
          <div class="audit-card-header">
            <span class="audit-icon">🖥️</span>
            <span class="audit-label">Kiosk Terminal</span>
          </div>
          <div class="audit-value-main">
            {{ coupon.redeemedDeviceId || 'Unknown Device' }}
          </div>
          <div class="audit-value-sub">
            Photobooth Kiosk Machine
          </div>
        </div>

        <!-- Issued Recipient -->
        <div class="audit-card">
          <div class="audit-card-header">
            <span class="audit-icon">👤</span>
            <span class="audit-label">Issued Recipient</span>
          </div>
          <div class="audit-value-main">
            {{ coupon.recipientName || 'General / Walk-in' }}
          </div>
          <div class="audit-value-sub">
            {{ coupon.recipientEmail || 'No email specified' }}
          </div>
        </div>

        <!-- Session ID (Full Width) -->
        <div class="audit-card full-span">
          <div class="audit-card-header">
            <span class="audit-icon">🆔</span>
            <span class="audit-label">Kiosk Session ID</span>
          </div>
          <div class="session-id-box">
            <span class="session-id-text font-mono">{{ coupon.redeemedSessionId || 'N/A' }}</span>
            <button class="session-copy-btn" (click)="copySessionId(coupon.redeemedSessionId)" *ngIf="coupon.redeemedSessionId" title="Copy Session ID">
              {{ copiedSessionId ? '✓ Copied' : '📋 Copy ID' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" (click)="closeRedemptionModal()">Close</button>
    </div>
  </div>
</div>
